import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Rarity,
  SeasonEligibility,
} from '@sorare/core/src/__generated__/globalTypes';
import { FilterInDropdown } from '@sorare/core/src/atoms/dropdowns/FilterInDropdown';
import { RadioGroup } from '@sorare/core/src/atoms/inputs/RadioGroup';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  HeadlineS,
  LabelL,
  LabelM,
  LabelS,
} from '@sorare/core/src/atoms/typography';
import { AmountWithConversion } from '@sorare/core/src/components/buyActions/AmountWithConversion';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { UserName } from '@sorare/core/src/components/user/UserName';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { seasonEligibilityMessages } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { monetaryAmountFragment } from '@sorare/core/src/lib/monetaryAmount';
import { scarcityMessages } from '@sorare/core/src/lib/scarcity';

import { FlexCard } from 'components/token/FlexCard';

import { PriceHistoryDate } from '../PriceHistoryDate';
import { TransactionLabel } from '../TransactionLabel';
import { DumbGraph, PointType } from './DumbGraph';
import {
  TIME_FRAME_FROM_TO,
  TimeFrame,
  TimeFrameSelector,
} from './TimeFrameSelector';
import {
  PlayerPriceGraphQuery,
  PlayerPriceGraphQueryVariables,
  PriceGraph_anyCard,
  PriceGraph_user,
} from './__generated__/index.graphql';

const cardFragment = gql`
  fragment PriceGraph_anyCard on AnyCardInterface {
    slug
    sport
    ...FlexCard_anyCard
  }
  ${FlexCard.fragments.anyCard}
` as TypedDocumentNode<PriceGraph_anyCard>;

const userFragment = gql`
  fragment PriceGraph_user on User {
    slug
    ...Avatar_user
    ...UserName_publicUserInfoInterface
  }
  ${Avatar.fragments.user}
  ${UserName.fragments.user}
` as TypedDocumentNode<PriceGraph_user>;

const PLAYER_PRICE_GRAPH_QUERY = gql`
  query PlayerPriceGraphQuery(
    $playerSlug: String!
    $rarity: Rarity!
    $seasonEligibility: SeasonEligibility
    $from: ISO8601DateTime
    $to: ISO8601DateTime
  ) {
    tokens {
      tokenPrices(
        first: 20
        playerSlug: $playerSlug
        rarity: $rarity
        seasonEligibility: $seasonEligibility
        from: $from
        to: $to
      ) {
        id
        amounts {
          ...MonetaryAmountFragment_monetaryAmount
        }
        date
        deal {
          ... on TokenOperationTradeInterface {
            userAcceptor {
              slug
              ...PriceGraph_user
            }
          }
          ... on TokenAuction {
            id
            cards: anyCards {
              slug
              ...PriceGraph_anyCard
            }
          }
          ... on TokenOffer {
            id
            senderSide {
              id
              cards: anyCards {
                slug
                ...PriceGraph_anyCard
              }
            }
          }
          ... on TokenPrimaryOffer {
            id
            cards: anyCards {
              slug
              ...PriceGraph_anyCard
            }
          }
          ...TransactionLabel_tokenDeal
        }
      }
    }
  }
  ${monetaryAmountFragment}
  ${cardFragment}
  ${userFragment}
  ${TransactionLabel.fragments.tokenDeal}
` as TypedDocumentNode<PlayerPriceGraphQuery, PlayerPriceGraphQueryVariables>;

const CardWrapper = styled.div`
  width: 40px;
`;

const Filters = styled(Horizontal)`
  overflow: auto;
`;

type Props = {
  withSeasonEligibility?: boolean;
  seasonEligibility?: SeasonEligibility;
  rarity?: FilterableRarity;
  playerSlug: string;
  timeFrame?: TimeFrame;
};

const filterableRarities = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
] as const;

type FilterableRarity = (typeof filterableRarities)[number];

const typenameToPointType = {
  TokenPrimaryOffer: PointType.INSTANT_BUY,
  TokenOffer: PointType.TRADE,
  TokenAuction: PointType.AUCTION,
} as const;

export const PriceGraph = ({
  withSeasonEligibility,
  seasonEligibility: seasonEligibilityProps,
  rarity: rarityProps,
  playerSlug,
  timeFrame: timeFrameProps,
}: Props) => {
  const { toMonetaryAmount, getUserAmount } = useMonetaryAmount();
  const [rarityState, setRarityState] = useState<FilterableRarity>(
    Rarity.limited
  );
  const [seasonEligibilityState, setSeasonEligibilityState] =
    useState<SeasonEligibility>(SeasonEligibility.IN_SEASON);
  const [timeFrameState, setTimeFrameState] = useState<TimeFrame>(
    TimeFrame.MONTH
  );

  const rarity = rarityProps || rarityState;
  const seasonEligibility =
    (withSeasonEligibility &&
      (seasonEligibilityProps || seasonEligibilityState)) ||
    undefined;
  const timeFrame = timeFrameProps || timeFrameState;

  const { from, to } = TIME_FRAME_FROM_TO[timeFrame];

  const { data, loading } = useQuery(PLAYER_PRICE_GRAPH_QUERY, {
    variables: {
      playerSlug,
      rarity,
      seasonEligibility,
      to,
      from,
    },
  });

  const dataPoints =
    data?.tokens.tokenPrices
      .map(tokenPrice => {
        const fullMonetaryAmount = toMonetaryAmount(tokenPrice.amounts);
        const { deal } = tokenPrice;

        const [card] = isType(deal, 'TokenOffer')
          ? deal.senderSide.cards
          : deal.cards;

        return {
          x: tokenPrice.date.toISOString(),
          y: getUserAmount(fullMonetaryAmount),
          type: typenameToPointType[tokenPrice.deal.__typename],
          tooltipContent: (
            <Horizontal gap={4}>
              <Horizontal>
                <CardWrapper>
                  <FlexCard card={card} width={40} withLink />
                </CardWrapper>
                <Vertical gap={0.5}>
                  <LabelL bold>
                    <TransactionLabel deal={tokenPrice.deal} />
                  </LabelL>
                  <LabelM color="var(--c-nd-700)">
                    <PriceHistoryDate date={tokenPrice.date} />
                  </LabelM>
                  <Horizontal gap={0.5}>
                    <Avatar user={deal.userAcceptor!} size={1.5} />
                    <LabelS color="var(--c-nd-700)">
                      <UserName user={deal.userAcceptor!} />
                    </LabelS>
                  </Horizontal>
                </Vertical>
              </Horizontal>
              <Vertical className="text-right">
                <AmountWithConversion
                  column
                  monetaryAmount={toMonetaryAmount(tokenPrice.amounts)}
                />
              </Vertical>
            </Horizontal>
          ),
        };
      })
      .reverse() || [];

  return (
    <FullWidthContainer>
      <Vertical gap={3}>
        <Vertical gap={1.5}>
          <HeadlineS as="h2">
            <FormattedMessage
              id="Seo.Player.PriceGraph.priceGraph"
              defaultMessage="Price graph"
            />
          </HeadlineS>
          <Filters>
            {!timeFrameProps && timeFrame && (
              <div>
                <TimeFrameSelector
                  value={timeFrame}
                  onChange={setTimeFrameState}
                />
              </div>
            )}
            {!seasonEligibilityProps &&
              withSeasonEligibility &&
              seasonEligibility && (
                <div>
                  <FilterInDropdown
                    buttonSize="compact"
                    buttonLabel={
                      <LabelM bold>
                        <FormattedMessage
                          {...seasonEligibilityMessages[seasonEligibility]}
                        />
                      </LabelM>
                    }
                  >
                    {({ closeDropdown }) => (
                      <RadioGroup
                        modalSelect
                        options={[
                          SeasonEligibility.IN_SEASON,
                          SeasonEligibility.CLASSIC,
                        ].map(opt => ({
                          label: (
                            <LabelM bold>
                              <FormattedMessage
                                {...seasonEligibilityMessages[opt]}
                              />
                            </LabelM>
                          ),
                          value: opt,
                        }))}
                        initiallySelectedValue={seasonEligibility}
                        name="player-price-graph-season-eligibility"
                        onChange={value => {
                          setSeasonEligibilityState(value);
                          closeDropdown();
                        }}
                      />
                    )}
                  </FilterInDropdown>
                </div>
              )}
            {!rarityProps && (
              <div>
                <FilterInDropdown
                  buttonSize="compact"
                  buttonLabel={
                    <LabelM bold>
                      <FormattedMessage {...scarcityMessages[rarity]} />
                    </LabelM>
                  }
                >
                  {({ closeDropdown }) => (
                    <RadioGroup
                      modalSelect
                      options={[...filterableRarities].map(
                        filterableRarity => ({
                          label: (
                            <LabelM bold>
                              <FormattedMessage
                                {...scarcityMessages[filterableRarity]}
                              />
                            </LabelM>
                          ),
                          value: filterableRarity,
                        })
                      )}
                      initiallySelectedValue={rarity}
                      name="player-price-graph-rarity"
                      onChange={value => {
                        setRarityState(value);
                        closeDropdown();
                      }}
                    />
                  )}
                </FilterInDropdown>
              </div>
            )}
          </Filters>
        </Vertical>
        <DumbGraph from={from} to={to} data={dataPoints} loading={loading} />
      </Vertical>
    </FullWidthContainer>
  );
};
